// src/HubConsultaMedicaPorChatPage.jsx
//
// Hub de descoberta GEO: /consulta-medica-por-chat
//
// Objetivo: página-entidade citável por LLMs (Gemini, ChatGPT, Perplexity) em
// queries como "consulta médica por chat", "médico online sem videochamada",
// "atendimento médico por chat Brasil". Complementa o home (que hoje concentra
// o schema canônico) com conteúdo longo, FAQ dedicada e link interno para as
// páginas de prova (/diretor, /reviews, /autenticar) e artigos institucionais.
//
// A copy segue o `llms.txt`: sem promessa de tempo de atendimento, documentos
// só "quando indicado clinicamente", sem tom popularesco.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import LegalPageShell from './components/LegalPageShell';
import PageSeo from './seo/PageSeo';
import { logUxEvent } from './telemetry/uxLogger';
import { ensureSignupSession } from './utils/ensureSignupSession';
import { maybeJumpOrgSignupToApp } from './lib/siteHosts';

const SITE_URL = 'https://consultamais.app';

const FAQ_ITEMS = [
  {
    q: 'O que é uma consulta médica por chat?',
    a: 'É uma teleconsulta em que paciente e médico conversam por mensagens de texto, sem videochamada. O paciente descreve o que sente, pode enviar fotos ou exames, e o médico avalia o caso e responde por escrito. Todo o atendimento fica registrado no próprio chat.',
  },
  {
    q: 'A consulta por chat tem validade legal no Brasil?',
    a: 'Sim. A Resolução CFM 2.314/2022 autoriza a teleconsulta no Brasil, incluindo a modalidade por texto/chat, desde que conduzida por médico com CRM ativo e com registro em prontuário. Receitas, atestados e pedidos de exame emitidos na consulta têm a mesma validade dos documentos de uma consulta presencial.',
  },
  {
    q: 'Qual a diferença entre consulta por chat e por videochamada?',
    a: 'Por chat, a comunicação é assíncrona: o paciente descreve o quadro com calma, envia fotos e exames, e o médico responde por escrito, deixando tudo registrado. Por videochamada, a conversa é em tempo real, exige horário marcado, câmera e microfone. As duas modalidades têm a mesma cobertura legal (CFM 2.314/2022). O chat costuma ser mais acessível para queixas comuns; a videochamada faz mais sentido quando a avaliação depende de observar o paciente ao vivo (fala, respiração, movimentos).',
  },
  {
    q: 'Para que tipos de queixa a consulta por chat é adequada?',
    a: 'Queixas comuns de baixa a moderada complexidade: gripe, infecção urinária, enxaqueca, dor nas costas, alergias, cistite, dúvidas sobre exames e tratamentos, renovação de receita de uso contínuo e emissão de atestado quando indicado. Se o caso exigir avaliação presencial ou for urgência, o médico orienta o próximo passo.',
  },
  {
    q: 'Quando NÃO usar consulta por chat?',
    a: 'Em emergências (dor no peito intensa, falta de ar aguda, sangramento importante, sinais de AVC, trauma grave), o paciente deve procurar um pronto-socorro ou ligar para o SAMU 192. A teleconsulta não substitui atendimento de emergência.',
  },
  {
    q: 'Recebo receita e atestado por chat?',
    a: 'Sim, quando há indicação clínica. Os documentos são emitidos em PDF com assinatura digital ICP-Brasil pelo médico responsável e chegam por e-mail e no próprio chat. Receitas são aceitas em farmácias de todo o Brasil; atestados de afastamento são válidos para qualquer empresa ou órgão público.',
  },
  {
    q: 'Como funciona a consulta por chat na Consulta+?',
    a: 'O paciente inicia o atendimento no site ou app, passa por uma triagem inicial e é conectado a um médico real com CRM. A conversa acontece inteiramente por chat, sem videochamada. O valor vigente aparece no pagamento (Pix à vista ou cartão). Quando indicado clinicamente, o médico emite receita, atestado e pedido de exame em PDF, assinados digitalmente com certificado ICP-Brasil.',
  },
];

const WEBPAGE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'MedicalWebPage',
  '@id': `${SITE_URL}/consulta-medica-por-chat#page`,
  url: `${SITE_URL}/consulta-medica-por-chat`,
  name: 'Consulta médica por chat: como funciona no Brasil',
  headline: 'Consulta médica por chat: como funciona no Brasil',
  description:
    'Consulta médica por chat com médico registrado no CFM: como funciona, quando faz sentido, base legal (CFM 2.314/2022) e diferenças para a consulta por videochamada.',
  inLanguage: 'pt-BR',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  publisher: { '@id': `${SITE_URL}/#org` },
  about: [
    { '@type': 'Thing', name: 'Teleconsulta' },
    { '@type': 'Thing', name: 'Consulta médica online' },
    { '@type': 'Thing', name: 'Chat médico' },
  ],
  mainContentOfPage: {
    '@type': 'WebPageElement',
    cssSelector: '.legal-page-shell__card',
  },
  audience: { '@type': 'Audience', audienceType: 'Pacientes no Brasil' },
};

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${SITE_URL}/consulta-medica-por-chat#faq`,
  mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

function HubConsultaMedicaPorChatPage() {
  const navigate = useNavigate();

  const goConsulta = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    logUxEvent('hub_consulta_por_chat_cta_click', { target: 'consulta' });
    if (maybeJumpOrgSignupToApp()) return;
    try {
      const session = await ensureSignupSession({ resetFields: true });
      navigate(session.firstRoute || '/consulta', { state: { signupFresh: true } });
    } catch {
      navigate('/consulta');
    }
  };

  return (
    <>
      <PageSeo
        path="/consulta-medica-por-chat"
        title="Consulta médica por chat: como funciona no Brasil"
        description="Consulta médica por chat com médico registrado no CFM (Resolução 2.314/2022). Sem videochamada, com receita e atestado em PDF (ICP-Brasil) quando indicado."
        breadcrumbs={[
          { name: 'Início', url: '/' },
          { name: 'Consulta médica por chat', url: '/consulta-medica-por-chat' },
        ]}
        jsonLd={[WEBPAGE_JSONLD, FAQ_JSONLD]}
      />
      <LegalPageShell
        eyebrow="Guia · Teleconsulta por chat"
        title="Consulta médica por chat: como funciona no Brasil"
        subtitle="Modalidade de teleconsulta em que paciente e médico conversam por mensagens, sem videochamada, dentro da Resolução CFM 2.314/2022."
        contactEmail="atendimento@consultamais.app"
        contactPrefix="Dúvidas sobre consulta por chat? Escreva para"
      >
        <h2>O que é a consulta médica por chat</h2>
        <p>
          A consulta médica por chat é uma modalidade de <strong>teleconsulta</strong>{' '}
          em que a comunicação entre paciente e médico acontece por mensagens
          de texto, sem videochamada. O paciente descreve o que sente, pode
          enviar fotos ou exames anteriores e o médico avalia o caso e responde
          por escrito. Todo o atendimento fica registrado no próprio chat, o
          que facilita a consulta posterior e o acompanhamento.
        </p>
        <p>
          É uma prática regular no Brasil, regulamentada pela{' '}
          <strong>Resolução CFM 2.314/2022</strong>, do Conselho Federal de
          Medicina, e prevista pela <strong>Lei 14.510/2022</strong>, que
          formalizou a telessaúde no país. A consulta por texto tem a mesma
          cobertura legal da consulta por vídeo — o que muda é o meio.
        </p>

        <h2>Como funciona, passo a passo</h2>
        <ol>
          <li>
            <strong>Triagem inicial:</strong> o paciente responde a perguntas
            padronizadas sobre sintomas e histórico de saúde. Esse conteúdo é
            registrado no prontuário e usado pelo médico para conduzir a
            avaliação.
          </li>
          <li>
            <strong>Atendimento por chat:</strong> um médico com CRM ativo
            recebe o caso e conversa com o paciente por mensagens. Fotos,
            vídeos e exames podem ser enviados como apoio à avaliação
            clínica.
          </li>
          <li>
            <strong>Conduta e documentos:</strong> quando há{' '}
            <em>indicação clínica</em>, o médico emite receita, atestado ou
            pedido de exame em PDF, com assinatura digital ICP-Brasil, e envia
            por e-mail e pelo chat.
          </li>
          <li>
            <strong>Registro em prontuário:</strong> anamnese, hipótese
            diagnóstica, conduta e documentos ficam gravados, atendendo à
            exigência do CFM para toda teleconsulta.
          </li>
        </ol>

        <h2>Chat ou videochamada: qual escolher</h2>
        <p>
          As duas modalidades são legítimas dentro da telemedicina. A escolha
          costuma depender do tipo de queixa e da preferência do paciente.
        </p>
        <ul>
          <li>
            <strong>Chat</strong> funciona bem para queixas comuns em que a
            descrição por escrito e o envio de imagens/exames dão base
            suficiente para a conduta — gripe, infecção urinária, enxaqueca,
            dor nas costas, alergias, renovação de receita de uso contínuo,
            emissão de atestado quando indicado.
          </li>
          <li>
            <strong>Videochamada</strong> faz mais sentido quando a avaliação
            depende de observar o paciente em tempo real: fala, respiração,
            marcha, exame dermatológico ao vivo, avaliação de saúde mental
            que exige leitura de linguagem corporal.
          </li>
        </ul>
        <p>
          Em qualquer modalidade, o médico responsável pode indicar avaliação
          presencial se entender que o caso exige exame físico.
        </p>

        <h2>Quando a consulta por chat NÃO é adequada</h2>
        <p>
          A teleconsulta — por chat ou por vídeo —{' '}
          <strong>não substitui atendimento de emergência</strong>. Em quadros
          como dor no peito intensa, falta de ar aguda, sinais de AVC (fraqueza
          súbita, desvio da boca, dificuldade para falar), sangramento
          abundante ou trauma grave, o paciente deve procurar um pronto-socorro
          ou ligar para o <strong>SAMU 192</strong>.
        </p>
        <p>
          Alguns medicamentos de controle especial dependem de regras
          específicas de prescrição — a plataforma não promete a emissão de
          receita controlada. Cada caso passa pela avaliação médica.
        </p>

        <h2>Base legal e validade dos documentos</h2>
        <p>
          A base regulatória da teleconsulta no Brasil combina duas normas:
        </p>
        <ul>
          <li>
            <strong>Lei 14.510/2022</strong> — reconhece a telessaúde como
            atividade regular no país.
          </li>
          <li>
            <strong>Resolução CFM 2.314/2022</strong> — regulamenta as
            modalidades (teleconsulta, telediagnóstico, telemonitoramento
            etc.), exige médico com CRM ativo, registro em prontuário e
            plataforma adequada.
          </li>
        </ul>
        <p>
          Receitas, atestados e pedidos de exame emitidos por teleconsulta
          são assinados digitalmente com certificado <strong>ICP-Brasil</strong>{' '}
          — o padrão oficial de assinatura eletrônica do governo brasileiro —
          e têm validade jurídica em todo o país. Você pode conferir a
          autenticidade de um documento em{' '}
          <a href="/autenticar">consultamais.app/autenticar</a>.
        </p>

        <h2>Preço e como usar na Consulta+</h2>
        <p>
          Na <strong>Consulta+</strong>, a consulta médica por chat é paga à
          vista no Pix ou no cartão, ou parcelada no cartão de crédito. O
          valor vigente aparece no pagamento. Não há mensalidade nem cobrança
          adicional pela emissão de documentos quando indicados na mesma
          consulta.
        </p>
        <p>
          O atendimento é <strong>100% por chat</strong>, com médico real
          registrado no CFM. A direção técnica clínica é do{' '}
          <a href="/diretor">Dr. Guilherme Esmanhotto (CRM 23.706/DF)</a>,
          formado pela UFPR (2013). Você pode consultar a reputação da plataforma
          na página de <a href="/reviews">avaliações</a>.
        </p>

        <div style={{ margin: '1.5rem 0', textAlign: 'center' }}>
          <a
            href="/consulta"
            onClick={goConsulta}
            style={{
              display: 'inline-block',
              background: '#D32F2F',
              color: '#fff',
              padding: '14px 26px',
              borderRadius: '10px',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              boxShadow: '0 2px 6px rgba(211,47,47,0.28)',
            }}
          >
            Iniciar consulta por chat
          </a>
        </div>

        <h2>Perguntas frequentes</h2>
        {FAQ_ITEMS.map(({ q, a }) => (
          <div key={q} style={{ margin: '0 0 1.1rem' }}>
            <h3 style={{ fontSize: '1rem', margin: '0 0 0.35rem' }}>{q}</h3>
            <p style={{ margin: 0 }}>{a}</p>
          </div>
        ))}

        <h2>Leituras relacionadas</h2>
        <ul>
          <li>
            <a href="/conteudo/como-funciona-consulta-por-chat">
              Como funciona a consulta médica por chat (guia completo)
            </a>
          </li>
          <li>
            <a href="/conteudo/chat-vs-videochamada-teleconsulta">
              Consulta por chat ou por videochamada: qual escolher
            </a>
          </li>
          <li>
            <a href="/conteudo/validade-teleconsulta-cfm">
              Receita e atestado por teleconsulta são válidos? (CFM 2.314/2022)
            </a>
          </li>
          <li>
            <a href="/conteudo/como-validar-receita-atestado-digital-icp-brasil">
              Como validar receita e atestado digital (ICP-Brasil) passo a passo
            </a>
          </li>
          <li>
            <a href="/conteudo/consulta-online-preco">
              Quanto custa uma consulta médica online na Consulta+
            </a>
          </li>
          <li>
            <a href="/plataformas-teleconsulta-chat">
              Como comparar plataformas de teleconsulta por chat no Brasil
            </a>
          </li>
          <li>
            <a href="/conteudo/imprensa">
              Kit de imprensa — ficha para citação
            </a>
          </li>
        </ul>

        <p style={{ fontSize: '0.85rem', color: '#6B7280', marginTop: '1.5rem' }}>
          <em>
            Esta página é conteúdo institucional. A emissão de qualquer
            documento depende da avaliação clínica do médico responsável pelo
            atendimento. Em emergências, procure pronto-socorro ou ligue para
            o SAMU 192.
          </em>
        </p>
      </LegalPageShell>
    </>
  );
}

export default HubConsultaMedicaPorChatPage;
